<template>
	<div
		class="flow-tag text-ellipsis"
		:size="size"
		:type="type"
		:shape="shape"
		:style="styleObj"
		:data-clickable="clickable"
		@click="handleClick($event)"
	>
		<!-- @slot Used to display text + Icon (optional)  -->
		<slot />
	</div>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";
import { CSSProperties } from "vue/types/jsx";

const SIZE = ["small", "medium", "large"] as const;
const TYPE = [
	"default",
	"primary",
	"secondary",
	"success",
	"error",
	"warning",
	"dark",
	"placeholder"
] as const;

export type TagSize = typeof SIZE[number];
export type TagType = typeof TYPE[number];

/**
 * Tags are used to label, categorize, or organize items using different keywords that describe them. Colour coded tags are helpful to quickly classify items and quickly catch user’s attention.
 */
export default defineComponent({
	name: "Tag",
	props: {
		/**
		 * 'small' | 'medium' | 'large'
		 */
		size: {
			type: String as PropType<TagSize>,
			default: "medium",
			validator(value: TagSize) {
				return SIZE.includes(value);
			}
		},

		/**
		 * 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'dark' | 'placeholder'
		 */
		type: {
			type: String as PropType<TagType>,
			default: "default",
			validator(value: TagType) {
				return TYPE.includes(value);
			}
		},

		/**
		 * tabindex number
		 *  */
		tabindex: {
			type: Number,
			default: -1
		},

		/**
		 * set background for tag
		 */
		background: {
			type: String as PropType<CSSProperties["background"]>,
			default: null
		},

		/**
		 * set color for text
		 */
		color: {
			type: String as PropType<CSSProperties["color"]>,
			default: null
		},

		/**
		 * 'curved' | 'rounded'
		 */
		shape: {
			type: String as PropType<"curved" | "rounded">,
			default: "curved",
			validator(value: "curved" | "rounded") {
				return ["rounded", "curved"].includes(value);
			}
		},

		/**
		 * This will add hover effect and cursor-pointer
		 */
		clickable: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		styleObj() {
			const style: CSSProperties = {};

			if (this.background) {
				style.background = this.background;
				style.color = this.color ? this.color : "var(--dashboard-font-dark)";
			}

			return style;
		}
	},
	methods: {
		handleClick(e: MouseEvent) {
			/**
			 *
			 * Click event : triggered when icon is clicked
			 *
			 * @event click
			 */
			this.$emit("click", e);
		}
	}
});
</script>
<style lang="scss">
@use "sass:math";
$types: "default", "primary", "secondary", "success", "error", "warning", "dark", "placeholder";

@mixin tag($background, $color, $primary, $hover) {
	div.flow-tag {
		@each $type in $types {
			&[type="#{$type}"] {
				background-color: map-get($background, $type);
				color: map-get($color, $type);
			}
		}
		&[type="placeholder"] {
			border: 2px dashed var(--dashboard-border-light);
		}

		&:not([disabled]) {
			&:focus {
				outline: none;
				box-shadow: inset 0 0 0 2px $primary;
			}
		}

		&:active,
		&:hover:not(:active) {
			&:before {
				background-color: $hover;
			}
		}
	}
}

@include tag(
	(
		"dark": var(--dashboard-element-dark),
		"default": var(--dashboard-element),
		"primary": var(--primary-400),
		"secondary": var(--dashboard-secondary-light),
		"success": var(--dashboard-success-light),
		"error": var(--dashboard-danger-light),
		"warning": var(--dashboard-warning-light),
		"placeholder": transparent
	),
	(
		"dark": var(--dashboard-font-dark),
		"default": var(--dashboard-font),
		"primary": var(--primary-200),
		"secondary": var(--dashboard-secondary),
		"success": var(--dashboard-success),
		"error": var(--dashboard-danger),
		"warning": var(--dashboard-warning),
		"placeholder": var(--dashboard-font)
	),
	var(--dashboard-primary),
	var(--dashboard-element-dark)
);
div.sideout-container {
	@include tag(
		(
			"dark": var(--slideout-element-dark),
			"default": var(--slideout-element),
			"primary": var(--slideout-primary-dark),
			"secondary": var(--slideout-secondary-light),
			"success": var(--slideout-success-light),
			"error": var(--slideout-danger-light),
			"warning": var(--slideout-warning-light)
		),
		(
			"dark": var(--slideout-font-dark),
			"default": var(--slideout-font),
			"primary": var(--slideout-primary),
			"secondary": var(--slideout-secondary),
			"success": var(--slideout-success),
			"error": var(--slideout-danger),
			"warning": var(--slideout-warning)
		),
		var(--slideout-primary),
		var(--dashboard-element-dark)
	);
}
$sizes: (
	"small": 18px,
	"medium": 24px,
	"large": 32px
);
$font-sizes: (
	"small": 10px,
	"medium": 12px,
	"large": 14px
);
$paddings: (
	"small": 4px 8px 4px 8px,
	"medium": 4px 8px 4px 8px,
	"large": 8px 12px 8px 12px
);

$margin-between: (
	"small": 4px,
	"medium": 4px,
	"large": 12px
);

div.flow-tag {
	flex-shrink: 0;
	display: inline-flex;
	align-items: center;
	position: relative;
	cursor: default;
	outline: none;
	&[shape="curved"] {
		border-radius: 4px;
	}
	@each $name, $size in $sizes {
		&[size="#{$name}"] {
			height: $size;
			&[shape="rounded"] {
				border-radius: math.div($size, 2);
			}
			font-size: map-get($font-sizes, $name);
			padding: map-get($paddings, $name);

			> * {
				margin-right: map-get($margin-between, $name);
				margin-left: map-get($margin-between, $name);

				&:last-child {
					margin-right: 0px;
				}

				&:only-child {
					margin-left: 0px;
				}
			}

			&:active,
			&:hover:not(:active) {
				&:before {
					border-radius: math.div($size, 2);
				}
			}
		}
	}

	&:active,
	&:hover:not(:active) {
		&:before {
			content: "";
			position: absolute;
			top: 0;
			right: 0;
			bottom: 0;
			left: 0;
			width: 100%;
			height: 100%;
		}
	}

	&:active {
		&:before {
			opacity: 0.08;
		}
	}

	&:hover:not(:active) {
		&:before {
			opacity: 0.04;
		}
	}

	&[data-clickable] {
		cursor: pointer;
	}
}

div.flow-tag-container {
	display: flex;
	flex-wrap: wrap;

	> div.flow-tag {
		margin: 8px 0px 8px 8px;

		&:last-child {
			margin: 8px 8px 8px 8px;
		}
	}
}
</style>
